import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ShoppingCart, Plus, Minus, X, Check } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PageSpinner } from '@/components/ui/Spinner'
import {
  useStorefrontCatalog, useStorefrontCheckout,
  type StorefrontItem,
} from '@/hooks/usePublicStorefront'
import type { FulfillmentType } from '@/types/app'

const ALL = '__all__'
const TAX_RATE = 0.07

const CHECKOUT_ERRORS: Record<string, string> = {
  not_found: "We couldn't find this store.",
  missing_contact: 'Name and phone number are required.',
  no_items: 'Your cart is empty.',
  invalid_item: 'One of the items in your cart is no longer available — please remove it and try again.',
  save_failed: 'Something went wrong placing your order. Please try again or call the store directly.',
}
function friendlyCheckoutError(message: string) {
  return CHECKOUT_ERRORS[message] ?? 'Something went wrong. Please try again.'
}

// Public storefront — no login, no passcode. Resolved entirely by the
// restaurant's slug in the URL today (/store/:slug), which is exactly the
// same lookup a future hostname-based resolution (a custom domain, or a
// restaurantname.rezzops.com subdomain) would key off — so this works right
// now with zero DNS setup, and doesn't need rebuilding once a real domain
// is pointed here later.
export function StorefrontPage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: catalog, isLoading, isError } = useStorefrontCatalog(slug)
  const checkout = useStorefrontCheckout()

  const [activeCategory, setActiveCategory] = useState(ALL)
  const [search, setSearch] = useState('')
  const [cart, setCart] = useState<Map<string, number>>(new Map())
  const [cartOpen, setCartOpen] = useState(false)
  const [step, setStep] = useState<'browse' | 'checkout' | 'done'>('browse')
  const [orderNumber, setOrderNumber] = useState<string | null>(null)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)

  const [form, setForm] = useState({
    client_name: '', client_phone: '', client_email: '',
    fulfillment_type: '' as FulfillmentType | '',
    requested_date: '', venue_address: '', notes: '',
  })
  function f(key: keyof typeof form, val: string) {
    setForm(p => ({ ...p, [key]: val }))
  }

  const itemById = useMemo(() => {
    const map = new Map<string, StorefrontItem>()
    for (const it of catalog?.items ?? []) map.set(it.id, it)
    return map
  }, [catalog])

  const q = search.trim().toLowerCase()
  const filteredItems = (catalog?.items ?? []).filter(it => {
    if (activeCategory !== ALL && it.category_id !== activeCategory) return false
    if (q && !it.name.toLowerCase().includes(q) && !(it.regional_name ?? '').toLowerCase().includes(q)) return false
    return true
  })

  const categoriesInUse = (catalog?.categories ?? []).filter(c =>
    (catalog?.items ?? []).some(it => it.category_id === c.id),
  )

  function addToCart(itemId: string) {
    setCart(prev => {
      const next = new Map(prev)
      next.set(itemId, (next.get(itemId) ?? 0) + 1)
      return next
    })
  }
  function setQty(itemId: string, qty: number) {
    setCart(prev => {
      const next = new Map(prev)
      if (qty <= 0) next.delete(itemId)
      else next.set(itemId, qty)
      return next
    })
  }

  const cartLines = [...cart.entries()].map(([itemId, qty]) => ({
    item: itemById.get(itemId),
    qty,
  })).filter((l): l is { item: StorefrontItem; qty: number } => !!l.item)
  const cartCount = cartLines.reduce((s, l) => s + l.qty, 0)
  const subtotal = cartLines.reduce((s, l) => s + l.item.price * l.qty, 0)
  const taxAmt = subtotal * TAX_RATE
  const total = subtotal + taxAmt

  async function handleSubmitOrder() {
    if (!slug) return
    setCheckoutError(null)
    if (!form.client_name.trim() || !form.client_phone.trim()) {
      setCheckoutError('missing_contact')
      return
    }
    try {
      const res = await checkout.mutateAsync({
        slug,
        client_name: form.client_name,
        client_phone: form.client_phone,
        client_email: form.client_email || undefined,
        fulfillment_type: form.fulfillment_type || undefined,
        requested_date: form.requested_date || undefined,
        venue_address: form.venue_address || undefined,
        notes: form.notes || undefined,
        items: cartLines.map(l => ({ menu_item_id: l.item.id, quantity: l.qty })),
      })
      setOrderNumber(res.order_number)
      setStep('done')
    } catch (e) {
      setCheckoutError(e instanceof Error ? e.message : 'save_failed')
    }
  }

  if (isLoading) return <PageSpinner />

  if (isError || !catalog) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
        <Card><CardContent><p className="text-sm text-stone-600">We couldn't find this store.</p></CardContent></Card>
      </div>
    )
  }

  const brand = catalog.restaurant.brand_colour_primary ?? '#c94a1a'

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          {catalog.restaurant.logo_url ? (
            <img src={catalog.restaurant.logo_url} alt={catalog.restaurant.name} className="h-10 w-10 rounded-lg object-cover shrink-0" />
          ) : (
            <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold shrink-0" style={{ backgroundColor: brand }}>
              {catalog.restaurant.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-stone-900 truncate">{catalog.restaurant.name}</p>
            <p className="text-2xs text-stone-400">Online Store</p>
          </div>
          {step === 'browse' && (
            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2 rounded-lg hover:bg-stone-100 text-stone-700"
            >
              <ShoppingCart size={20} />
              {cartCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 h-5 w-5 rounded-full text-white text-2xs font-bold flex items-center justify-center"
                  style={{ backgroundColor: brand }}
                >
                  {cartCount}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-4">
        {step === 'browse' && (
          <>
            <div className="mb-3 space-y-2">
              <input
                type="search"
                placeholder="Search items…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                <button
                  onClick={() => setActiveCategory(ALL)}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    activeCategory === ALL ? 'text-white border-transparent' : 'bg-white border-stone-200 text-stone-600'
                  }`}
                  style={activeCategory === ALL ? { backgroundColor: brand } : undefined}
                >
                  All
                </button>
                {categoriesInUse.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setActiveCategory(c.id)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      activeCategory === c.id ? 'text-white border-transparent' : 'bg-white border-stone-200 text-stone-600'
                    }`}
                    style={activeCategory === c.id ? { backgroundColor: brand } : undefined}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>

            {filteredItems.length === 0 ? (
              <p className="text-sm text-stone-400 text-center py-12">No items match.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {filteredItems.map(item => {
                  const qtyInCart = cart.get(item.id) ?? 0
                  return (
                    <Card key={item.id} padding="none" className="overflow-hidden flex flex-col">
                      <div className="aspect-square bg-stone-100 flex items-center justify-center overflow-hidden">
                        {item.images[0] ? (
                          <img src={item.images[0]} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-3xl text-stone-300">🍽️</span>
                        )}
                      </div>
                      <div className="p-2.5 flex flex-col flex-1 gap-1.5">
                        <p className="text-sm font-medium text-stone-900 leading-tight">{item.name}</p>
                        {item.regional_name && <p className="text-2xs text-stone-400">{item.regional_name}</p>}
                        <div className="mt-auto flex items-center justify-between gap-1">
                          <span className="text-sm font-semibold text-stone-900">${item.price.toFixed(2)}</span>
                          {qtyInCart === 0 ? (
                            <button
                              onClick={() => addToCart(item.id)}
                              className="p-1.5 rounded-full text-white"
                              style={{ backgroundColor: brand }}
                            >
                              <Plus size={14} />
                            </button>
                          ) : (
                            <div className="flex items-center gap-1">
                              <button onClick={() => setQty(item.id, qtyInCart - 1)} className="p-1 rounded-full border border-stone-300 text-stone-600">
                                <Minus size={12} />
                              </button>
                              <span className="text-xs font-semibold w-4 text-center">{qtyInCart}</span>
                              <button onClick={() => setQty(item.id, qtyInCart + 1)} className="p-1 rounded-full border border-stone-300 text-stone-600">
                                <Plus size={12} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  )
                })}
              </div>
            )}
          </>
        )}

        {step === 'checkout' && (
          <Card>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-stone-900">Checkout</h2>
                <button onClick={() => setStep('browse')} className="text-xs text-stone-400 hover:text-stone-700">Back to store</button>
              </div>

              <div className="border border-stone-200 rounded-lg divide-y divide-stone-100">
                {cartLines.map(l => (
                  <div key={l.item.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="text-stone-700">{l.item.name} × {l.qty}</span>
                    <span className="font-medium text-stone-900">${(l.item.price * l.qty).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-stone-500"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-stone-500"><span>+ 7% tax</span><span>${taxAmt.toFixed(2)}</span></div>
                <div className="flex justify-between font-bold text-stone-900 pt-1 border-t border-stone-100"><span>Total</span><span>${total.toFixed(2)}</span></div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input label="Your Name *" value={form.client_name} onChange={e => f('client_name', e.target.value)} />
                <Input label="Phone Number *" type="tel" value={form.client_phone} onChange={e => f('client_phone', e.target.value)} />
              </div>
              <Input label="Email (optional)" type="email" value={form.client_email} onChange={e => f('client_email', e.target.value)} />

              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-stone-700">Delivery or Pickup</span>
                <div className="flex gap-2">
                  {(['pickup', 'delivery'] as const).map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => f('fulfillment_type', opt)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        form.fulfillment_type === opt ? 'text-white border-transparent' : 'bg-white border-stone-200 text-stone-600'
                      }`}
                      style={form.fulfillment_type === opt ? { backgroundColor: brand } : undefined}
                    >
                      {opt === 'pickup' ? "I'll pick up" : 'Please deliver'}
                    </button>
                  ))}
                </div>
              </div>

              {form.fulfillment_type === 'delivery' && (
                <Input label="Delivery Address" value={form.venue_address} onChange={e => f('venue_address', e.target.value)} />
              )}
              <Input label="Requested Date" type="date" value={form.requested_date} onChange={e => f('requested_date', e.target.value)} />

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-stone-700">Notes (optional)</label>
                <textarea
                  value={form.notes}
                  onChange={e => f('notes', e.target.value)}
                  rows={2}
                  className="border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400 resize-none"
                />
              </div>

              {checkoutError && <p className="text-sm text-danger">{friendlyCheckoutError(checkoutError)}</p>}

              <Button className="w-full" onClick={handleSubmitOrder} loading={checkout.isPending}>
                Place Order — ${total.toFixed(2)}
              </Button>
              <p className="text-2xs text-stone-400 text-center">
                The store will confirm your order and payment details shortly after you submit.
              </p>
            </CardContent>
          </Card>
        )}

        {step === 'done' && (
          <Card>
            <CardContent className="text-center py-8 space-y-3">
              <div className="mx-auto h-12 w-12 rounded-full bg-success-light flex items-center justify-center">
                <Check size={24} className="text-success-dark" />
              </div>
              <h2 className="font-semibold text-stone-900 text-lg">Order placed!</h2>
              <p className="text-sm text-stone-500">Your order number is</p>
              <p className="font-mono text-lg font-bold text-stone-900">{orderNumber}</p>
              <p className="text-xs text-stone-400 max-w-xs mx-auto">
                {catalog.restaurant.name} will reach out shortly to confirm your order and arrange payment.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Cart drawer */}
      {cartOpen && step === 'browse' && (
        <div className="fixed inset-0 z-30 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setCartOpen(false)} />
          <div className="relative w-full sm:max-w-sm bg-white h-full flex flex-col shadow-2xl">
            <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between">
              <h3 className="font-semibold text-stone-900">Your Cart</h3>
              <button onClick={() => setCartOpen(false)} className="p-1 rounded hover:bg-stone-100 text-stone-400"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {cartLines.length === 0 ? (
                <p className="text-sm text-stone-400 text-center py-8">Your cart is empty.</p>
              ) : cartLines.map(l => (
                <div key={l.item.id} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">{l.item.name}</p>
                    <p className="text-xs text-stone-400">${l.item.price.toFixed(2)} each</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => setQty(l.item.id, l.qty - 1)} className="p-1 rounded-full border border-stone-300 text-stone-600"><Minus size={12} /></button>
                    <span className="text-sm font-semibold w-5 text-center">{l.qty}</span>
                    <button onClick={() => setQty(l.item.id, l.qty + 1)} className="p-1 rounded-full border border-stone-300 text-stone-600"><Plus size={12} /></button>
                  </div>
                  <span className="text-sm font-semibold text-stone-900 w-14 text-right shrink-0">${(l.item.price * l.qty).toFixed(2)}</span>
                </div>
              ))}
            </div>
            {cartLines.length > 0 && (
              <div className="p-4 border-t border-stone-200 space-y-3">
                <div className="flex justify-between text-sm font-semibold text-stone-900">
                  <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
                </div>
                <Button className="w-full" onClick={() => { setCartOpen(false); setStep('checkout') }}>
                  Checkout
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
